import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectDefaultCreationCrmOrgId, selectUserInfoData } from '../../reducers/userInfoReducer';
import { connectSfStages } from '../../reducers/united-canvas/unitedCanvasReducer';
import { useSfAddFunnelToTheCanvas } from '../../components/pages/funnel-map-flow/add-funnel/useSfAddFunnelToTheCanvas';
import { v4 as uuidv4 } from 'uuid';
import { StageType } from '../../types/enums/StageType';
import { FunnelType } from '../../types/enums/FunnelType';
import { useAddHealthcareMetadataElements } from './useAddHealthcareMetadataElements';
import { getFullMetadataElementId } from '../../components/pages/funnel-map-flow/metadata-elements/metadataElementUtils';

// Helper function to create funnel details with fresh UUIDs
const createHealthcareFunnelDetails = () => {
  // Generate fresh UUIDs for all internal IDs
  const leadFunnelStageIds = {
    newInquiry: uuidv4(),
    scheduled: uuidv4(),
    intakeComplete: uuidv4(),
    convertedToPatient: uuidv4(),
    noShow: uuidv4(),
  };

  const leadFunnelExitCriteriaIds = {
    newInquiryToScheduled: uuidv4(),
    scheduledToIntakeComplete: uuidv4(),
    scheduledToNoShow: uuidv4(),
    intakeCompleteToConverted: uuidv4(),
  };

  const opportunityFunnelStageIds = {
    treatmentPlanned: uuidv4(),
    inTreatment: uuidv4(),
    completed: uuidv4(),
    recallFollowup: uuidv4(),
    newTreatment: uuidv4(),
    notProceeding: uuidv4(),
  };

  const opportunityFunnelExitCriteriaIds = {
    treatmentPlannedToInTreatment: uuidv4(),
    inTreatmentToCompleted: uuidv4(),
    completedToNotProceeding: uuidv4(),
    recallToCompleted: uuidv4(),
    recallToNewTreatment: uuidv4(),
    newTreatmentToInTreatment: uuidv4(),
  };

  return {
    leadFunnel: {
      leadingObject: {
        objectName: 'Lead',
        fieldName: 'Status',
        _leadingFieldLabels: ['Lead', 'Status'],
        _leadingFieldId: 'Lead.Status',
      },
      stages: [
        {
          stageName: 'New Inquiry',
          _stageId: leadFunnelStageIds.newInquiry,
          stageDescription: 'New Inquiry',
          _branchIndex: -1,
          _stageColumnIndex: 0,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: leadFunnelExitCriteriaIds.newInquiryToScheduled,
              _nextStageId: leadFunnelStageIds.scheduled,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'Scheduled',
          _stageId: leadFunnelStageIds.scheduled,
          stageDescription: 'Appointment Scheduled',
          _branchIndex: -1,
          _stageColumnIndex: 1,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: leadFunnelExitCriteriaIds.scheduledToIntakeComplete,
              _nextStageId: leadFunnelStageIds.intakeComplete,
              isAutoMoveToNextStage: false,
            },
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: leadFunnelExitCriteriaIds.scheduledToNoShow,
              _nextStageId: leadFunnelStageIds.noShow,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'Intake Complete',
          _stageId: leadFunnelStageIds.intakeComplete,
          stageDescription: 'Intake Complete',
          _branchIndex: -1,
          _stageColumnIndex: 2,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: leadFunnelExitCriteriaIds.intakeCompleteToConverted,
              _nextStageId: leadFunnelStageIds.convertedToPatient,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'Converted to Patient',
          _stageId: leadFunnelStageIds.convertedToPatient,
          stageDescription: 'Converted to Patient',
          _branchIndex: -1,
          _stageColumnIndex: 3,
          exitCriteria: [],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'No Show',
          _stageId: leadFunnelStageIds.noShow,
          stageDescription: 'No Show',
          _branchIndex: -2,
          _stageColumnIndex: 1,
          exitCriteria: [],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.NURTURING,
          additionalFields: [],
        },
      ],
      plugins: {},
      funnelLinks: [],
      stageMetadata: [
        {
          leadingFieldId: 'Lead.Status',
          stageName: 'New Inquiry',
          converted: false,
        },
        {
          leadingFieldId: 'Lead.Status',
          stageName: 'Scheduled',
          converted: false,
        },
        {
          leadingFieldId: 'Lead.Status',
          stageName: 'Intake Complete',
          converted: false,
        },
        {
          leadingFieldId: 'Lead.Status',
          stageName: 'Converted to Patient',
          converted: false,
        },
        {
          leadingFieldId: 'Lead.Status',
          stageName: 'No Show',
          converted: false,
        },
      ],
    },
    opportunityFunnel: {
      leadingObject: {
        objectName: 'Opportunity',
        fieldName: 'StageName',
        _leadingFieldLabels: ['Opportunity', 'Stage'],
        _leadingFieldId: 'Opportunity.StageName',
      },
      stages: [
        {
          stageName: 'Treatment Planned',
          _stageId: opportunityFunnelStageIds.treatmentPlanned,
          stageDescription: 'Treatment Planned',
          _branchIndex: -2,
          _stageColumnIndex: 0,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: opportunityFunnelExitCriteriaIds.treatmentPlannedToInTreatment,
              _nextStageId: opportunityFunnelStageIds.inTreatment,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'In Treatment',
          _stageId: opportunityFunnelStageIds.inTreatment,
          stageDescription: 'In Treatment',
          _branchIndex: -2,
          _stageColumnIndex: 1,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: opportunityFunnelExitCriteriaIds.inTreatmentToCompleted,
              _nextStageId: opportunityFunnelStageIds.completed,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'Completed',
          _stageId: opportunityFunnelStageIds.completed,
          stageDescription: 'Treatment Completed',
          _branchIndex: -2,
          _stageColumnIndex: 2,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: opportunityFunnelExitCriteriaIds.completedToNotProceeding,
              _nextStageId: opportunityFunnelStageIds.notProceeding,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'Recall/Follow-up',
          _stageId: opportunityFunnelStageIds.recallFollowup,
          stageDescription: 'Follow-up',
          _branchIndex: -1,
          _stageColumnIndex: 3,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: opportunityFunnelExitCriteriaIds.recallToCompleted,
              _nextStageId: opportunityFunnelStageIds.completed,
              isAutoMoveToNextStage: false,
            },
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: opportunityFunnelExitCriteriaIds.recallToNewTreatment,
              _nextStageId: opportunityFunnelStageIds.newTreatment,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'New Treatment',
          _stageId: opportunityFunnelStageIds.newTreatment,
          stageDescription: 'New Treatment',
          _branchIndex: -1,
          _stageColumnIndex: 2,
          exitCriteria: [
            {
              criteria: [],
              criteriaLogic: '',
              _exitCriteriaId: opportunityFunnelExitCriteriaIds.newTreatmentToInTreatment,
              _nextStageId: opportunityFunnelStageIds.inTreatment,
              isAutoMoveToNextStage: false,
            },
          ],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
        {
          stageName: 'Not Proceeding',
          _stageId: opportunityFunnelStageIds.notProceeding,
          stageDescription: 'Not Proceeding',
          _branchIndex: -3,
          _stageColumnIndex: 3,
          exitCriteria: [],
          entryInits: [],
          exitInits: [],
          apexClasses: [],
          assignmentRules: [] as [],
          stageType: StageType.REGULAR,
          additionalFields: [],
        },
      ],
      plugins: {},
      stageMetadata: [
        {
          leadingFieldId: 'Opportunity.StageName',
          stageName: 'Treatment Planned',
          type: 'Open',
          forecastCategory: 'Pipeline',
          probability: 0,
        },
        {
          leadingFieldId: 'Opportunity.StageName',
          stageName: 'In Treatment',
          type: 'Open',
          forecastCategory: 'Pipeline',
          probability: 0,
        },
        {
          leadingFieldId: 'Opportunity.StageName',
          stageName: 'Completed',
          type: 'Open',
          forecastCategory: 'Pipeline',
          probability: 0,
        },
        {
          leadingFieldId: 'Opportunity.StageName',
          stageName: 'Recall/Follow-up',
          type: 'Open',
          forecastCategory: 'Pipeline',
          probability: 0,
        },
        {
          leadingFieldId: 'Opportunity.StageName',
          stageName: 'New Treatment',
          type: 'Open',
          forecastCategory: 'Pipeline',
          probability: 0,
        },
        {
          leadingFieldId: 'Opportunity.StageName',
          stageName: 'Not Proceeding',
          type: 'Open',
          forecastCategory: 'Pipeline',
          probability: 0,
        },
      ],
    },
    stageIds: {
      leadFunnel: leadFunnelStageIds,
      opportunityFunnel: opportunityFunnelStageIds,
    },
  };
};

/**
 * Hook for adding healthcare funnels to the funnel map
 * Uses onCreateSfFunnel for proper backend persistence instead of just Redux state
 */
export const useAddInitialElementsToMap = () => {
  const dispatch = useDispatch();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const userInfo = useSelector(selectUserInfoData);
  const { onCreateSfFunnel } = useSfAddFunnelToTheCanvas(crmOrgId || '');
  const { addHealthcareMetadataElements } = useAddHealthcareMetadataElements();

  const addInitialElementsToMap = useCallback(async () => {
    try {
      // Validate environment context
      if (!crmOrgId || !userInfo?.id || !userInfo?.account?.id) {
        throw new Error('Missing required environment context: crmOrgId, userId, or accountId');
      }

      console.log('Creating healthcare funnels with current environment context:', {
        crmOrgId,
        userId: userInfo.id,
        accountId: userInfo.account.id,
      });

      // Generate fresh funnel details with new UUIDs
      const funnelDetails = createHealthcareFunnelDetails();

      // Create the Lead funnel (New Patient Intake)
      console.log('Creating New Patient Intake funnel...');
      const leadFunnel = await onCreateSfFunnel({
        funnelName: 'New Patient Intake',
        funnelDetails: funnelDetails.leadFunnel,
        position: { column: 0, row: 1 },
      });

      // Create the Opportunity funnel (Active Patient Care)
      console.log('Creating Active Patient Care funnel...');
      const opportunityFunnel = await onCreateSfFunnel({
        funnelName: 'Active Patient Care',
        funnelDetails: funnelDetails.opportunityFunnel,
        position: { column: 4, row: 1 },
      });

      console.log('Successfully created healthcare funnels:', {
        leadFunnel: leadFunnel.name,
        opportunityFunnel: opportunityFunnel.name,
      });

      // Add healthcare metadata elements to the canvas
      console.log('Adding healthcare metadata elements...');
      addHealthcareMetadataElements();

      // Connect "Converted to Patient" stage to "Treatment Planned" stage
      console.log('Connecting "Converted to Patient" to "Treatment Planned" stages...');

      const funnelConnectionPayload = {
        stageA: {
          funnelId: leadFunnel.id,
          stageId: funnelDetails.stageIds.leadFunnel.convertedToPatient,
        },
        stageB: {
          funnelId: opportunityFunnel.id,
          stageId: funnelDetails.stageIds.opportunityFunnel.treatmentPlanned,
          funnelType: FunnelType.SALESFORCE,
        },
      };

      dispatch(connectSfStages(funnelConnectionPayload));
      console.log('Successfully connected funnels');

      const targetElementId = getFullMetadataElementId({
        crmOrgId,
        metadataId: 'salesforce.Flow.instance.Count_Days_Since_Last_Activity',
      });

      const metadataConnectionPayload = {
        stageA: {
          funnelId: opportunityFunnel.id,
          stageId: funnelDetails.stageIds.opportunityFunnel.completed,
        },
        stageB: {
          funnelId: targetElementId,
          funnelType: FunnelType.METADATA_ELEMENT,
        },
      };

      dispatch(connectSfStages(metadataConnectionPayload));
      console.log('Successfully connected stage to metadata element');

      return [leadFunnel, opportunityFunnel];
    } catch (error) {
      console.error('Failed to create healthcare funnels:', error);
      throw error;
    }
  }, [dispatch, crmOrgId, userInfo, onCreateSfFunnel, addHealthcareMetadataElements]);

  return { addInitialElementsToMap };
};
